import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePreferenceStore } from '@/stores/usePreferenceStore';
import { ensureLocaleFont } from '@/styles/fonts';
import { ensureLocaleTranslations } from '@/i18n';
import BrandLoading from '@/components/organisms/BrandLoading';
import { Button } from '@/components/ui/button';

/**
 * 언어가 바뀌면 i18next · <html lang> · 서체를 맞추고 서버 데이터를 다시 받는다
 * (API 는 X-Glow-Locale 로 해석한 문자열을 내려주므로 쿼리 무효화가 필요하다).
 */
export default function LocaleSync({ children }: { children?: ReactNode }) {
  const locale = usePreferenceStore((state) => state.locale);
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const target = locale ?? (i18n.resolvedLanguage as typeof locale) ?? 'en';
  const [failedLocale, setFailedLocale] = useState<string>();
  const pending = i18n.language !== target;

  useEffect(() => {
    let current = true;
    const synchronize = async () => {
      try {
        if (i18n.language !== target) {
          await ensureLocaleTranslations(target);
          if (!current || usePreferenceStore.getState().locale !== locale)
            return;
          await i18n.changeLanguage(target);
          if (!current) return;
          document.documentElement.lang = target;
          void ensureLocaleFont(target);
          await queryClient.invalidateQueries();
        }
        if (!current) return;
        document.documentElement.lang = target;
        void ensureLocaleFont(target);
      } catch {
        if (current) setFailedLocale(target);
      }
    };
    void synchronize();
    return () => {
      current = false;
    };
  }, [i18n, locale, queryClient, target]);

  return (
    <>
      <div
        className="contents"
        inert={pending}
        aria-hidden={pending || undefined}
        style={pending ? { visibility: 'hidden' } : undefined}
      >
        {children}
      </div>
      {pending && children && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gp-cream px-6">
          {failedLocale === target ? (
            <div className="max-w-sm space-y-4 text-center text-gp-ink-900">
              <p role="alert">{t('status.error')}</p>
              <Button type="button" onClick={() => window.location.reload()}>
                {t('entry.retry')}
              </Button>
            </div>
          ) : (
            <BrandLoading />
          )}
        </div>
      )}
    </>
  );
}
